import { useEffect } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { AVATAR_ASSETS, BRAND_COLORS, type MeResponse } from '@lk/shared';
import { BrandImg } from '../../components/BrandImg';
import { avatarIcon } from '../../components/Icon';
import { SyncIndicator } from '../../components/SyncIndicator';
import { cn } from '../../lib/cn';
import { useLogout } from '../auth/useAuth';
import { useParentStore } from './parentStore';

interface ParentTab {
  to: '/parent' | '/parent/skills' | '/parent/mistakes' | '/parent/wallet' | '/parent/settings' | '/parent/review';
  label: string;
  exact?: boolean;
  /** Вкладка лише для батька: глядач бачить тільки статистику. */
  parentOnly?: boolean;
}

const TABS: ParentTab[] = [
  { to: '/parent', label: 'Зведення', exact: true },
  { to: '/parent/skills', label: 'Мапа навичок' },
  { to: '/parent/mistakes', label: 'Помилки' },
  { to: '/parent/wallet', label: 'Гаманець', parentOnly: true },
  { to: '/parent/settings', label: 'Налаштування', parentOnly: true },
  { to: '/parent/review', label: 'Модерація', parentOnly: true },
];

export function ParentLayout({ me }: { me: MeResponse }): React.JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const selected = useParentStore((s) => s.selectedLearnerId);
  const select = useParentStore((s) => s.select);
  const logout = useLogout();

  const learners = me.learners;
  const viewer = me.user.role === 'viewer';
  const tabs = viewer ? TABS.filter((t) => !t.parentOnly) : TABS;

  useEffect(() => {
    if (learners.length === 0) return;
    if (!selected || !learners.some((l) => l.id === selected)) {
      select(learners[0]!.id);
    }
  }, [learners, selected, select]);

  return (
    <div className="lk-shell-wide lk-safe flex min-h-dvh flex-col gap-4 pt-3 pb-10">
      {/*
        Кнопки «назад» тут навмисно немає: кабінет — це домівка батька, а не
        надбудова над ареною. Стрілка вела на дитячий екран, де батько бачив
        чужий гаманець під власним іменем і не міг зробити нічого корисного.
      */}
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-black">{viewer ? 'Статистика' : 'Кабінет батьків'}</h1>
        <div className="flex-1" />
        <SyncIndicator />
        <button
          type="button"
          className="lk-chip cursor-pointer"
          onClick={() => logout.mutate()}
        >
          Вийти
        </button>
      </header>

      {learners.length > 1 ? (
        <nav aria-label="Вибір дитини" className="flex flex-wrap gap-2">
          {learners.map((learner) => (
            <button
              key={learner.id}
              type="button"
              onClick={() => select(learner.id)}
              aria-pressed={selected === learner.id}
              className={cn(
                'lk-panel-tight flex items-center gap-2 px-3 py-2 text-sm font-black',
                selected === learner.id && 'border-[var(--lk-gold)]',
              )}
            >
              <BrandImg
                src={AVATAR_ASSETS[learner.avatarKey]?.path ?? '/brand/avatars/avatar-child.png'}
                alt={learner.displayName}
                fallbackIcon={avatarIcon(learner.avatarKey)}
                colors={[BRAND_COLORS.cyan, BRAND_COLORS.magenta]}
                shape="circle"
                className="h-8 w-8"
              />
              {learner.displayName}
              <span className="text-white/40">{learner.grade} кл.</span>
            </button>
          ))}
        </nav>
      ) : null}

      <nav aria-label="Розділи кабінету" className="lk-scroll-x -mx-1 flex gap-2 px-1 pb-1">
        {tabs.map((tab) => {
          const active = tab.exact ? pathname === tab.to : pathname.startsWith(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              className={cn(
                'lk-chip shrink-0 px-3 py-2 text-xs',
                active && 'border-[var(--lk-gold)] text-[var(--lk-gold)]',
              )}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      <Outlet />
    </div>
  );
}

/** Хук-помічник: активна дитина або порожньо. */
export function useSelectedLearner(me: MeResponse): MeResponse['learners'][number] | null {
  const selected = useParentStore((s) => s.selectedLearnerId);
  return me.learners.find((l) => l.id === selected) ?? me.learners[0] ?? null;
}
